<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  recommendationHouseId,
  VISIT_DETAIL_TEXT,
} from '../utils/visitDetailPresenter';
import ExtraVisitCard from './ExtraVisitCard.vue';
import ExtraVisitsEmptyState from './ExtraVisitsEmptyState.vue';
import ReviewExtraVisitsModal from './ReviewExtraVisitsModal.vue';

const props = defineProps({
  visit: { type: Object, default: () => ({}) },
  recommendations: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  visitorId: { type: [String, Number], default: '' },
});

const emit = defineEmits([
  'confirm',
  'openProfile',
  'openListing',
  'reload',
  'intentClick',
  'reviewOpen',
]);

const EXTRA_VISITS_COPY = Object.freeze({
  iconLabel: 'Atalho de visitas extras',
});

const showReview = ref(false);
const selected = ref(new Set());

const allIds = computed(() =>
  props.recommendations.map(item => recommendationHouseId(item)).filter(Boolean)
);

const selectedRecommendations = computed(() =>
  props.recommendations.filter(item =>
    selected.value.has(recommendationHouseId(item))
  )
);

const scheduleLabel = computed(() =>
  selected.value.size === 1
    ? 'Agendar visita'
    : `Agendar visitas (${selected.value.size})`
);

watch(
  () => props.recommendations,
  () => {
    selected.value = new Set(allIds.value);
  },
  { immediate: true }
);

const toggle = id => {
  const next = new Set(selected.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selected.value = next;
};

const openReview = () => {
  emit('intentClick', selectedRecommendations.value);
  showReview.value = true;
  emit('reviewOpen', selectedRecommendations.value);
};

const confirm = () => {
  emit('confirm', selectedRecommendations.value);
};

const closeAfterSave = () => {
  if (!props.saving) showReview.value = false;
};

watch(
  () => props.saving,
  (saving, previous) => {
    if (!saving && previous) closeAfterSave();
  }
);
</script>

<template>
  <LaisCard
    :title="VISIT_DETAIL_TEXT.EXTRA_VISITS"
    prefix-icon="i-lucide-calendar-plus"
  >
    <div v-if="loading" class="grid gap-3">
      <div
        v-for="index in 3"
        :key="index"
        class="h-20 animate-pulse rounded-lais-sm bg-lais-bg-soft"
      />
    </div>

    <div v-else-if="recommendations.length" class="grid gap-4">
      <div class="grid gap-1">
        <span
          class="grid size-8 place-items-center rounded-lais-sm bg-lais-primary-50 text-lais-primary"
          :aria-label="EXTRA_VISITS_COPY.iconLabel"
        >
          <span class="i-lucide-calendar-days size-4" aria-hidden="true" />
        </span>
        <p class="text-[18px] font-semibold text-lais-text">
          {{ VISIT_DETAIL_TEXT.EXTRA_VISITS_SUBTITLE }}
        </p>
      </div>

      <div class="grid gap-3">
        <ExtraVisitCard
          v-for="item in recommendations"
          :key="recommendationHouseId(item)"
          :item="item"
          :selected="selected.has(recommendationHouseId(item))"
          @toggle="toggle"
          @open-listing="emit('openListing', $event)"
        />
      </div>

      <LaisPrimaryButton
        icon="i-lucide-calendar-plus"
        :disabled="selected.size === 0"
        @click="openReview"
      >
        {{ scheduleLabel }}
      </LaisPrimaryButton>
    </div>

    <ExtraVisitsEmptyState
      v-else
      :visitor-id="visitorId"
      @open-profile="emit('openProfile')"
      @reload="emit('reload')"
    />

    <ReviewExtraVisitsModal
      :show="showReview"
      :visit="visit"
      :recommendations="selectedRecommendations"
      :saving="saving"
      @close="showReview = false"
      @confirm="confirm"
    />
  </LaisCard>
</template>
